import usePermissions from '@/hooks/usePermissions';
import { useGetOrganizationDetailsQuery, useGetOrganizationEmailSettingsQuery } from '@/services/organizations';
import { useDeleteOrganizationPricingHoldbacksMutation } from '@/services/pricing';
import { StandardHoldbackSelfServiceClientData } from '@/types/Holdbacks';
import { Alert, Box, Button, Card, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';

import { OrganizationEmailSettingCategories, OrganizationType, ProgramType, UserPermissions } from 'types';

import { ActiveStandardHoldbacksTable } from './ActiveStandardHoldbacksTable';
import { ArchiveStandardHoldbackModal } from './ArchiveStandardHoldbackModal';
import { EditStandardHoldbackModal } from './EditStandardHoldbackModal';

interface ActiveStandardHoldbacksProps {
  activeHoldbacks: StandardHoldbackSelfServiceClientData[];
  alias: string;
  programType: ProgramType;
  isLoading: boolean;
}

export const ActiveStandardHoldbacks = ({
  activeHoldbacks,
  alias,
  programType,
  isLoading,
}: ActiveStandardHoldbacksProps) => {
  const [selectedHoldback, setSelectedHoldback] = useState<StandardHoldbackSelfServiceClientData | null>(null);

  const [deleteOrganizationPricingHoldbackMutation, { isLoading: isArchiving }] =
    useDeleteOrganizationPricingHoldbacksMutation();

  const { data: emailSettings } = useGetOrganizationEmailSettingsQuery({
    alias,
  });
  const { data: organization } = useGetOrganizationDetailsQuery({ alias });
  const hasSignatoryEmail =
    (emailSettings?.emailSettings?.find((s) => s.category === OrganizationEmailSettingCategories.signatory)?.emails
      ?.length ?? 0) > 0;
  const isMaterialsVendor = organization?.organizationTypes?.includes(OrganizationType.materials);
  const permissions = usePermissions();
  const isSignatory = permissions?.includes(UserPermissions.lightReachOrganizationSignatory) ?? false;
  const canEdit = isSignatory && hasSignatoryEmail;
  const {
    isOpen: isEditHoldbackModalOpen,
    handleOpen: handleOpenEditHoldbackModal,
    handleClose: handleCloseEditHoldbackModal,
  } = useOpenClose();
  const {
    isOpen: isArchiveHoldbackModalOpen,
    handleOpen: handleOpenArchiveHoldbackModal,
    handleClose: handleCloseArchiveHoldbackModal,
  } = useOpenClose();

  const onArchive = useCallback(
    (holdback: StandardHoldbackSelfServiceClientData) => {
      setSelectedHoldback(holdback);
      handleOpenArchiveHoldbackModal();
    },
    [handleOpenArchiveHoldbackModal],
  );

  const handleArchiveHoldback = useCallback(async () => {
    if (!selectedHoldback) return;
    try {
      await deleteOrganizationPricingHoldbackMutation({
        alias,
        id: selectedHoldback.id.toString(),
        programType,
      }).unwrap();
      toast.success('Holdback archived successfully');
      handleCloseArchiveHoldbackModal();
    } catch (error) {
      console.error(error);
      toast.error('Error archiving Holdback');
    }
  }, [
    selectedHoldback,
    deleteOrganizationPricingHoldbackMutation,
    alias,
    programType,
    handleCloseArchiveHoldbackModal,
  ]);

  return (
    <>
      {!hasSignatoryEmail && !isMaterialsVendor && (
        <Alert variant="warning" message="Holdback settings cannot be edited without any signatory emails." />
      )}
      {isEditHoldbackModalOpen && (
        <EditStandardHoldbackModal
          programType={programType}
          alias={alias}
          isOpen={isEditHoldbackModalOpen}
          handleClose={handleCloseEditHoldbackModal}
          activeHoldbacks={activeHoldbacks}
        />
      )}
      {selectedHoldback && (
        <ArchiveStandardHoldbackModal
          state={selectedHoldback.state}
          isOpen={isArchiveHoldbackModalOpen}
          handleClose={handleCloseArchiveHoldbackModal}
          onConfirmArchive={handleArchiveHoldback}
          isLoading={isArchiving}
        />
      )}
      <Card padding="xs">
        <Box padding="lg" alignItems="center" direction="row" justifyContent="space-between">
          <Box fontWeight="medium" fontSize="lg">
            Active
          </Box>
          <Button
            variant="primary"
            tone="primary"
            size="sm"
            aria-label="Edit Active Holdbacks"
            iconPrefix="pencil"
            isDisabled={![ProgramType.solar, ProgramType.newHomes].includes(programType) || !canEdit}
            onClick={handleOpenEditHoldbackModal}
          >
            Edit
          </Button>
        </Box>
        <Box margin="0 md">
          <ActiveStandardHoldbacksTable
            holdbacks={activeHoldbacks}
            isLoading={isLoading}
            onArchive={onArchive}
            canArchive={isSignatory}
          />
        </Box>
      </Card>
    </>
  );
};
